<div class="cj">

  <!-- ── Page Header ───────────────────────────────── -->
  <div class="cj__header">
    <div>
      <h1 class="cj__title">Create Job</h1>
      <p class="cj__subtitle">
        Assign field work to your team. Add the location so workers
        can navigate directly to the job site.
      </p>
    </div>
    <a routerLink="/jobs/all" class="cj__back-btn">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
        <path d="M19 12H5M5 12L12 19M5 12L12 5"
              stroke="currentColor" stroke-width="2"
              stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      All Jobs
    </a>
  </div>

  <!-- ── Error banner ──────────────────────────────── -->
  <div class="cj__error" *ngIf="error">
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.8"/>
      <path d="M12 8v4M12 16h.01"
            stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
    </svg>
    {{ error }}
  </div>

  <!-- ── Form ──────────────────────────────────────── -->
  <div class="cj__card">

    <!-- Job Title -->
    <div class="cj__field">
      <label class="cj__label">
        Job Title <span class="cj__required">*</span>
      </label>
      <input
        class="cj__input"
        type="text"
        [(ngModel)]="title"
        placeholder="e.g. Pipeline Inspection — OML 118"
        autocomplete="off"/>
    </div>

    <!-- Description -->
    <div class="cj__field">
      <label class="cj__label">
        Description <span class="cj__required">*</span>
      </label>
      <textarea
        class="cj__input cj__textarea"
        [(ngModel)]="description"
        placeholder="Describe the work to be done, tools required, safety considerations..."
        rows="4">
      </textarea>
    </div>

    <!-- Location with Google Places -->
    <div class="cj__field">
      <label class="cj__label">
        Job Location <span class="cj__required">*</span>
      </label>

      <div class="cj__location-wrap">
        <div class="cj__location-input-wrap">
          <svg class="cj__location-icon"
               width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M12 2C8.1 2 5 5.1 5 9C5 14.2 12 22 12 22C12 22 19 14.2 19 9C19 5.1 15.9 2 12 2Z"
                  stroke="currentColor" stroke-width="1.8"/>
            <circle cx="12" cy="9" r="2.5" stroke="currentColor" stroke-width="1.8"/>
          </svg>

          <!-- Input bound to Google Places Autocomplete -->
          <input
            #locationInput
            class="cj__input cj__input--location"
            type="text"
            [(ngModel)]="locationName"
            placeholder="Search for a location or address..."
            autocomplete="off"
            (ngModelChange)="locationConfirmed = false; latitude = null; longitude = null"/>

          <!-- Clear button -->
          <button
            class="cj__location-clear"
            *ngIf="locationName"
            (click)="clearLocation()"
            type="button"
            title="Clear location">
            ×
          </button>
        </div>

        <!-- Manual entry button for remote/unnamed locations -->
        <button
          class="cj__manual-btn"
          type="button"
          (click)="enterCoordinatesManually()"
          title="Enter GPS coordinates manually for remote sites">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.8"/>
            <path d="M12 8v4M12 16h.01"
                  stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
          Remote site
        </button>
      </div>

      <!-- Coordinate confirmation indicator -->
      <div class="cj__coord-status cj__coord-status--confirmed"
           *ngIf="locationConfirmed && latitude !== null">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
          <path d="M20 6L9 17L4 12"
                stroke="currentColor" stroke-width="2.5"
                stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        GPS coordinates captured — workers can navigate to this location.
        <span class="cj__coord-values">
          {{ latitude?.toFixed(5) }}, {{ longitude?.toFixed(5) }}
        </span>
      </div>

      <div class="cj__coord-status cj__coord-status--missing"
           *ngIf="locationName && !locationConfirmed">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.8"/>
          <path d="M12 8v4M12 16h.01"
                stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>
        Select a result from the dropdown to capture GPS coordinates,
        or use "Remote site" for unmapped locations.
      </div>
    </div>

    <!-- Date -->
    <div class="cj__field">
      <label class="cj__label">
        Assigned Date <span class="cj__required">*</span>
      </label>
      <input
        class="cj__input"
        type="date"
        [(ngModel)]="assignedDate"/>
    </div>

    <!-- Assign Worker -->
    <div class="cj__field">
      <label class="cj__label">Assign to Worker (optional)</label>

      <div class="cj__loading-workers" *ngIf="loadingWorkers">
        <div class="cj__spinner-small"></div>
        Loading workers...
      </div>

      <select
        class="cj__input cj__select"
        [(ngModel)]="assignedUserId"
        *ngIf="!loadingWorkers">
        <option [value]="null">— Unassigned —</option>
        <option *ngFor="let worker of workers" [value]="worker.id">
          {{ worker.fullName }}
          <span *ngIf="worker.email"> ({{ worker.email }})</span>
        </option>
      </select>

      <p class="cj__field-hint" *ngIf="!loadingWorkers && workers.length === 0">
        No workers found. Invite workers first from the Workers section.
      </p>
    </div>

    <!-- Actions -->
    <div class="cj__actions">
      <a routerLink="/jobs/all" class="cj__cancel-btn">Cancel</a>

      <button
        class="cj__submit-btn"
        type="button"
        (click)="createJob()"
        [disabled]="!isFormValid || loading">
        <span class="cj__spinner" *ngIf="loading"></span>
        <svg *ngIf="!loading" width="15" height="15" viewBox="0 0 24 24" fill="none">
          <path d="M12 5v14M5 12h14"
                stroke="currentColor" stroke-width="2.5"
                stroke-linecap="round"/>
        </svg>
        {{ loading ? 'Creating Job…' : 'Create Job' }}
      </button>
    </div>

  </div>

  <!-- ── How location works info card ─────────────────── -->
  <div class="cj__info-card">
    <div class="cj__info-icon">📍</div>
    <div class="cj__info-body">
      <h3 class="cj__info-title">How Job Location Works</h3>
      <p class="cj__info-text">
        When you search for a location and select it from the dropdown, the GPS
        coordinates are saved with the job. Field workers can then tap
        <strong>Navigate</strong> in the mobile app to get turn-by-turn
        directions to the job site.
      </p>
      <p class="cj__info-text">
        For remote or unmapped locations (offshore platforms, bush sites, oil
        fields), tap <strong>Remote site</strong> and enter the GPS coordinates
        manually. Your team in the field can provide these coordinates.
      </p>
    </div>
  </div>

</div>